import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import createTheme from '@mui/material/styles/createTheme';
import TablePagination from '@mui/material/TablePagination';
import { ThemeProvider, useTheme } from '@mui/material';
import { MaterialTableProps } from './MaterialTable.model';
import {
  MaterialReactTable,
  MRT_Column,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_ColumnOrderState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_VisibilityState,
  useMaterialReactTable
} from 'material-react-table';
import { MRT_Localization_CS } from 'material-react-table/locales/cs';
import { csCZ } from '@mui/material/locale';
import { CustomButton } from '../CustomButton/CustomButton';
import { Flex, Space } from '@mantine/core';
import Box from '@mui/material/Box';
import { IconFileTypeCsv, IconFileTypeXls, IconTrash } from '@tabler/icons-react';
import { CustomActionIcon } from '../CustomActionIcon/CustomActionIcon';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { ExportMenu, ExportMode } from './ExportMenu';

// Filter variants that trigger immediate search (on value change)
const IMMEDIATE_FILTER_VARIANTS = ['select', 'date', 'date-range'];
// [fix DAP-017] Multi-select se NECOMMITUJE po každém kliknutí. Re-fetch/re-render (nová data
// tabulky) totiž rozbije otevřenou nabídku MUI Selectu — uskočí doleva (ztráta kotvy) a hlavně
// se zavře, takže nešlo zaškrtnout více hodnot (chovalo se jako single-select). Zaškrtávání
// proto jen aktualizuje zobrazený (pending) stav; nabídka zůstane otevřená a na místě a lze
// vybrat víc hodnot. Skutečné filtrování (commit + re-fetch) proběhne až při zavření nabídky
// / popoveru filtru — viz muiFilterTextFieldProps.SelectProps.onClose.
const COMMIT_ON_CLOSE_FILTER_VARIANTS = ['multi-select'];

// [DAP-019] Built-in MRT sloupec pro výběr řádků. V pořadí sloupců je vždy první.
const ROW_SELECT_COLUMN_ID = 'mrt-row-select';

// [DAP-019] ID sloupce z jeho definice (id má přednost před accessorKey).
const getColumnId = (col: MRT_ColumnDef<any>): string =>
  (col.id ?? (col.accessorKey as string)) as string;

// [DAP-019] Výchozí pořadí sloupců, které MRT používá bez uloženého nastavení:
// nejprve výběr řádků, poté sloupce v pořadí definice.
const buildDefaultColumnOrder = (columns: MRT_ColumnDef<any>[]): MRT_ColumnOrderState => [
  ROW_SELECT_COLUMN_ID,
  ...columns.map(getColumnId).filter(Boolean)
];

// [DAP-019] Sloučí uložené pořadí s aktuální sadou sloupců:
// - uložené ID, které stále existuje, si drží uloženou pozici,
// - nové (neznámé) sloupce se doplní na svou výchozí pozici,
// - odebrané sloupce vypadnou.
// Výsledek je robustní vůči přidání/odebrání sloupců (rozumný fallback na default).
const mergeColumnOrder = (
  savedOrder: string[] | undefined,
  columns: MRT_ColumnDef<any>[]
): MRT_ColumnOrderState => {
  const defaultOrder = buildDefaultColumnOrder(columns);
  if (!savedOrder || savedOrder.length === 0) {
    return defaultOrder;
  }
  const defaultSet = new Set(defaultOrder);
  // Uložené sloupce, které stále existují (v uloženém pořadí).
  const merged = savedOrder.filter((id) => defaultSet.has(id));
  const mergedSet = new Set(merged);
  // Doplníme sloupce, které v uloženém pořadí chybí, na jejich výchozí pozici.
  defaultOrder.forEach((id) => {
    if (!mergedSet.has(id)) {
      merged.push(id);
    }
  });
  return merged;
};

export const DEFAULT_COLUMN_VISIBILITY: MRT_VisibilityState = {
  holderIc: false,
  holderLegalForm: false,
  contactEmails: false,
  briefDescription: false,
  relatedResolution: false,
  resultQuantification: false,
  partners: false,
  followUpPrkkPriorityArea: false,
  followUpSrrcrStrategicTarget: false,
  initiative: false,
  preprojectPreparationStart: false,
  implementationLength: false,
  financialEnd: false,
  // [DAP-021] Datum vydání právního aktu — nový volitelný sloupec, default skrytý (uživatel
  // si ho zapne přes „Zobrazit/skrýt sloupce"); podporuje filtr (date-range) i řazení.
  legalActDate: false,
  sustainabilityPeriodStart: false,
  sustainabilityPeriodEnd: false,
  totalEstimatedCosts: false,
  preparationCosts: false,
  implementationCosts: false,
  fundingMainSource: false
};

const MaterialTable = ({
  columns,
  tableData,
  externalColumnFilters,
  initialSorting,
  initialPagination,
  initialGlobalFilter,
  initialColumnVisibility,
  onColumnVisibilityChange,
  initialColumnOrder,
  onColumnOrderChange,
  onTableChange,
  handleExportRowsExcel,
  handleExportRowsCsv,
  handleExportLastExcel,
  handleExportLastCsv,
  hasExportSettings,
  deleteProjects,
  clearFilters,
  isLoading,
  canModify
}: MaterialTableProps) => {
  const globalTheme = useTheme();
  // Počáteční stav se přebírá z rodiče (obnova z URL – DAP-016). Inicializuje se
  // jen jednou při mountu; runtime změny řídí interní stav a onTableChange.
  const [pagination, setPagination] = useState<MRT_PaginationState>(
    initialPagination ?? {
      pageIndex: 0,
      pageSize: 10
    }
  );
  const [sorting, setSorting] = useState<MRT_SortingState>(initialSorting ?? []);
  // Committed filters - these trigger API calls
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    externalColumnFilters ?? []
  );
  // Pending filters - for text/number inputs, not yet committed
  const [pendingFilters, setPendingFilters] = useState<MRT_ColumnFiltersState>(
    externalColumnFilters ?? []
  );
  // [fix DAP-016] Poslední hodnota columnFilters, kterou tabulka sama poslala nahoru
  // (přes onTableChange). Slouží k rozpoznání „round-tripu": když se externalColumnFilters
  // vrátí jako přesně tato hodnota, nejde o externí push (dlaždice/URL), ale o náš vlastní
  // stav — nesmíme jím přepsat (a shodit) uživatelovu právě probíhající editaci filtru.
  const lastEmittedFiltersRef = useRef<string>(JSON.stringify(externalColumnFilters ?? []));
  // [fix DAP-017] Vždy aktuální snapshoty pending/committed filtrů. Commit multi-selectu se
  // spouští z onClose nabídky (viz níže); čtení přes ref eliminuje zastaralý closure.
  const pendingFiltersRef = useRef<MRT_ColumnFiltersState>(externalColumnFilters ?? []);
  pendingFiltersRef.current = pendingFilters;
  const committedFiltersRef = useRef<MRT_ColumnFiltersState>(externalColumnFilters ?? []);
  committedFiltersRef.current = columnFilters;
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter ?? '');
  const [pendingGlobalFilter, setPendingGlobalFilter] = useState(initialGlobalFilter ?? '');
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    initialColumnVisibility ?? DEFAULT_COLUMN_VISIBILITY
  );
  // [DAP-019] Pořadí sloupců (per-user). Inicializuje se sloučením uloženého pořadí
  // s aktuální sadou sloupců (fallback na default pro neznámé/odebrané sloupce).
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>(() =>
    mergeColumnOrder(initialColumnOrder, columns)
  );

  // Build a map of column IDs to their filter variants
  const columnFilterVariants = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    columns.forEach((col: MRT_ColumnDef<any>) => {
      const key = col.accessorKey as string;
      if (key) {
        map[key] = col.filterVariant;
      }
    });
    return map;
  }, [columns]);

  // Check if a column uses immediate filter (select, date, etc.)
  const isImmediateFilter = useCallback(
    (columnId: string) => {
      const variant = columnFilterVariants[columnId];
      return variant && IMMEDIATE_FILTER_VARIANTS.includes(variant);
    },
    [columnFilterVariants]
  );

  // [fix DAP-017] Filtry, které se commitují až při zavření (multi-select).
  const isCommitOnCloseFilter = useCallback(
    (columnId: string) => {
      const variant = columnFilterVariants[columnId];
      return variant && COMMIT_ON_CLOSE_FILTER_VARIANTS.includes(variant);
    },
    [columnFilterVariants]
  );

  // [fix DAP-017] Commit filtrů = jejich promítnutí do skutečného filtrování (re-fetch).
  const commitColumnFilters = useCallback((filters: MRT_ColumnFiltersState) => {
    setColumnFilters(filters);
  }, []);

  // Handle filter changes from MRT
  const handleColumnFiltersChange = useCallback(
    (
      updater: MRT_ColumnFiltersState | ((old: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)
    ) => {
      // Get the new filters
      const newFilters = typeof updater === 'function' ? updater(pendingFilters) : updater;

      // Always update pending filters (for UI display)
      setPendingFilters(newFilters);

      // Find which filter changed
      const changedFilter = newFilters.find((newF) => {
        const oldFilter = pendingFilters.find((oldF) => oldF.id === newF.id);
        return !oldFilter || JSON.stringify(oldFilter.value) !== JSON.stringify(newF.value);
      });

      // Also check for removed filters
      const removedFilter = pendingFilters.find(
        (oldF) => !newFilters.find((newF) => newF.id === oldF.id)
      );

      const affectedId = changedFilter?.id ?? removedFilter?.id;

      // [fix DAP-017] Multi-select: pouze aktualizuj zobrazený (pending) stav. Necommitujeme
      // (žádný re-fetch/re-render s novými daty), takže otevřená nabídka MUI Selectu zůstane
      // otevřená a na místě a uživatel může zaškrtnout více hodnot. Commit (re-fetch) proběhne
      // až při zavření nabídky filtru — viz muiFilterTextFieldProps.SelectProps.onClose.
      if (affectedId && isCommitOnCloseFilter(affectedId)) {
        return;
      }

      // If a filter was removed, commit immediately
      if (removedFilter) {
        commitColumnFilters(newFilters);
        return;
      }

      // If the changed filter is an immediate type (select, date), commit right away
      if (changedFilter && isImmediateFilter(changedFilter.id)) {
        commitColumnFilters(newFilters);
      }
      // For text/number filters, wait for Enter key (handled via muiFilterTextFieldProps)
    },
    [pendingFilters, isImmediateFilter, isCommitOnCloseFilter, commitColumnFilters]
  );

  // Commit pending filters to actual filters (called on Enter key)
  const commitPendingFilters = useCallback(() => {
    commitColumnFilters(pendingFilters);
  }, [pendingFilters, commitColumnFilters]);

  // [fix DAP-017] Commit multi-select filtru při zavření jeho nabídky/popoveru. Čteme aktuální
  // hodnoty přes ref (bez zastaralého closure) a commitujeme jen při reálné změně oproti již
  // commitnutému stavu — zavření beze změny tak nevyvolá zbytečný re-fetch.
  const commitFiltersOnClose = useCallback(() => {
    const pending = pendingFiltersRef.current;
    if (JSON.stringify(pending) !== JSON.stringify(committedFiltersRef.current)) {
      commitColumnFilters(pending);
    }
  }, [commitColumnFilters]);

  // Handle global filter change
  const handleGlobalFilterChange = useCallback((value: string) => {
    setPendingGlobalFilter(value);
    // Don't commit immediately - wait for Enter
  }, []);

  // Commit global filter (called on Enter key)
  const commitGlobalFilter = useCallback(() => {
    setGlobalFilter(pendingGlobalFilter);
  }, [pendingGlobalFilter]);

  // Get IDs of columns that must never be hidden (enableHiding: false)
  const nonHideableColumnIds = useMemo(() => {
    const ids = new Set<string>();
    columns.forEach((col: MRT_ColumnDef<any>) => {
      if (col.enableHiding === false) {
        const colId = (col.id || col.accessorKey) as string;
        if (colId) {
          ids.add(colId);
        }
      }
    });
    return ids;
  }, [columns]);

  // Remove non-hideable columns from visibility state (ensure they stay visible)
  const sanitizeVisibility = useCallback(
    (visibility: MRT_VisibilityState): MRT_VisibilityState => {
      if (nonHideableColumnIds.size === 0) return visibility;
      const sanitized = { ...visibility };
      nonHideableColumnIds.forEach((id) => {
        delete sanitized[id];
      });
      return sanitized;
    },
    [nonHideableColumnIds]
  );

  // Sync initial column visibility from API when it changes
  useEffect(() => {
    if (initialColumnVisibility) {
      setColumnVisibility(sanitizeVisibility(initialColumnVisibility));
    }
  }, [initialColumnVisibility]);

  // Notify parent about column visibility changes
  const handleColumnVisibilityChange = (
    updater: MRT_VisibilityState | ((old: MRT_VisibilityState) => MRT_VisibilityState)
  ) => {
    setColumnVisibility((prev) => {
      const newVisibility = sanitizeVisibility(
        typeof updater === 'function' ? updater(prev) : updater
      );
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(newVisibility);
      }
      return newVisibility;
    });
  };

  // [DAP-019] Sync uloženého pořadí sloupců z API. Při načtení (nebo změně počtu
  // sloupců podle role/číselníků) sloučíme uložené pořadí s aktuální sadou sloupců.
  useEffect(() => {
    setColumnOrder(mergeColumnOrder(initialColumnOrder, columns));
  }, [initialColumnOrder, columns]);

  // [DAP-019] Změna pořadí sloupců (drag-reorder). Uložíme lokálně a propíšeme
  // do rodiče, který pořadí persistuje per-user přes ColumnSettings API.
  const handleColumnOrderChange = (
    updater: MRT_ColumnOrderState | ((old: MRT_ColumnOrderState) => MRT_ColumnOrderState)
  ) => {
    setColumnOrder((prev) => {
      const newOrder = typeof updater === 'function' ? updater(prev) : updater;
      if (onColumnOrderChange) {
        onColumnOrderChange(newOrder);
      }
      return newOrder;
    });
  };

  // Sync external column filters (dashboard tiles / URL restore) with internal state.
  // [fix DAP-016] Rozlišujeme externí push od round-tripu vlastního stavu: pokud
  // externalColumnFilters odpovídá poslední hodnotě, kterou tabulka sama vydala
  // (lastEmittedFiltersRef), NEDĚLÁME nic — jinak by se dvoucestná vazba zacyklila a
  // přepisovala uživatelovu editaci filtru (nešlo měnit ani mazat filtr po obnově z URL).
  // Reset provedeme jen u skutečně externí změny (klik na dlaždici, obnova z URL).
  useEffect(() => {
    if (!externalColumnFilters) {
      return;
    }
    const incoming = JSON.stringify(externalColumnFilters);
    if (incoming === lastEmittedFiltersRef.current) {
      return;
    }
    lastEmittedFiltersRef.current = incoming;
    setColumnFilters(externalColumnFilters);
    setPendingFilters(externalColumnFilters);
  }, [externalColumnFilters]);

  // Sync pagination řízené rodičem (DAP-016): page-overflow fallback (rodič srazí
  // stránku mimo rozsah na poslední validní) a reset filtrů. Aktualizujeme jen při
  // reálné změně, aby nedošlo k zacyklení.
  useEffect(() => {
    if (
      initialPagination &&
      (initialPagination.pageIndex !== pagination.pageIndex ||
        initialPagination.pageSize !== pagination.pageSize)
    ) {
      setPagination(initialPagination);
    }
  }, [initialPagination]);

  useEffect(() => {
    // [fix DAP-016] Zaznamenáme, co tabulka posílá nahoru, ať následný návrat téže hodnoty
    // v externalColumnFilters rozpoznáme jako round-trip (viz sync efekt výše) a neshodíme
    // tím uživatelovu editaci.
    lastEmittedFiltersRef.current = JSON.stringify(columnFilters);
    if (onTableChange) {
      onTableChange({
        pagination,
        sorting,
        columnFilters,
        globalFilter
      });
    }
  }, [pagination, sorting, columnFilters, globalFilter]);

  const clearTableFilters = () => {
    setPagination({
      pageIndex: 0,
      pageSize: 10
    });
    setSorting([]);
    setColumnFilters([]);
    setPendingFilters([]);
    setGlobalFilter('');
    setPendingGlobalFilter('');
    setRowSelection({});
    if (clearFilters) {
      clearFilters();
    }
  };

  const theme = createTheme(
    {
      palette: {
        mode: globalTheme.palette.mode,
        primary: {
          main: '#b8b3e1'
        },
        info: {
          main: '#b8b3e1'
        }
      },
      components: {
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.MuiTableRow-root:hover td': {
                backgroundColor: 'rgba(240,239,250,0.97)'
              }
            }
          }
        }
      }
    },
    csCZ
  );

  const materialTable = useMaterialReactTable({
    columns,
    data: tableData.data,
    state: {
      pagination,
      sorting,
      columnFilters: pendingFilters, // Show pending filters in UI
      globalFilter: pendingGlobalFilter, // Show pending global filter in UI
      rowSelection,
      columnVisibility,
      columnOrder,
      isLoading: isLoading
    },
    localization: MRT_Localization_CS,
    enableFilterMatchHighlighting: false,
    enablePagination: false,
    enableStickyHeader: true,
    // [fix DAP-018] Stabilní šířky sloupců při řazení. Bez toho běží tabulka v defaultním
    // 'semantic' layoutu (table-layout: auto), takže prohlížeč počítá šířku každého sloupce
    // z obsahu právě zobrazených řádků. Po seřazení se vymění řádky stránky → změní se délky
    // textů → přepočítají se šířky → všechny sloupce se vodorovně posunou a seřazený sloupec
    // „uteče". table-layout: fixed přinutí prohlížeč použít pevné šířky sloupců (MRT je zapisuje
    // z `size`, viz ProjectsColumns) a obsah buněk šířku neovlivní → sloupce zůstanou na místě.
    // Nepoužíváme layoutMode 'grid', ten renderuje buňky přes flex-row a rozbil by víceřádkové
    // buňky (kontaktní e-maily, návaznosti), které stackují <Typography> pod sebe.
    muiTableProps: {
      sx: {
        tableLayout: 'fixed'
      }
    },
    // [fix DAP-018] Názvy (hlavičky) sloupců musí být VŽDY plně čitelné — nikdy oříznuté a
    // vždy s celými slovy vcelku. MRT dává hlavičce s < 20 znaky white-space:nowrap +
    // overflow:hidden + text-overflow:ellipsis (index.esm.js), takže se v pevném
    // (table-layout:fixed) úzkém sloupci ořízne na „Čís…" (ovládací ikony sloupce — řazení,
    // filtr, menu, přesun — přitom trvale zabírají ~90 px). Přepíšeme obě vnitřní obálky
    // hlavičky: zalamujeme název na víc řádků, ale JEN mezi celými slovy (white-space:normal,
    // BEZ word-break — slovo se nikdy nerozdělí uprostřed) a vypneme ořez. Šířka sloupce
    // zůstává pevná — roste jen výška řádku hlavičky, takže oprava proti „uskakování" platí dál.
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content-Labels': {
          overflow: 'visible'
        },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'normal'
        }
      }
    },
    // [DAP-018] Minimální šířka sloupce 250 px. `minSize` se přes TanStack `getSize()` promítne
    // do šířky sloupce i bez resizingu (šířka = clamp(size, minSize, maxSize)), takže žádný
    // datový sloupec není užší než 250 px: sloupce se `size` < 250 se dorovnají na 250, širší
    // (kvůli max. 3řádkovým hlavičkám) si drží svou větší šířku. MRT slučuje defaultColumn přes
    // své defaulty (Object.assign) — zůstává size:180 i maxSize:1000, mění se jen minSize.
    defaultColumn: {
      minSize: 250
    },
    // Výběrový sloupec (checkboxy, `mrt-row-select`) je systémový display sloupec se `size` 60 a
    // bez vlastního `minSize` — zdědil by globální minSize 250 a nabobtnal na 250 px. Vyjmeme ho
    // vlastním nízkým minSize, ať zůstane úzký.
    displayColumnDefOptions: {
      'mrt-row-select': {
        minSize: 40
      }
    },
    initialState: {
      density: 'compact',
      // [fix DAP-016] Když se globální hledání obnoví z URL/sessionStorage (neprázdný
      // initialGlobalFilter), rozbalíme vyhledávací pole. Jinak zůstane sbalené (MRT ho
      // renderuje v Collapse s unmountOnExit) a tlačítko pro jeho rozbalení je při
      // neprázdném globalFilter deaktivované (`disabled: !!globalFilter`) — hledání by pak
      // po návratu na seznam nešlo změnit ani zrušit.
      showGlobalFilter: Boolean(initialGlobalFilter)
    },
    onColumnVisibilityChange: handleColumnVisibilityChange,
    // COLUMN ORDERING (DAP-019): ruční přetahování pořadí sloupců, per-user persistence.
    enableColumnOrdering: true,
    onColumnOrderChange: handleColumnOrderChange,
    // SORTING (DAP-018): řazení až dle dvou sloupců zároveň.
    // enableMultiSort umožní víceúrovňové řazení, maxMultiSortColCount=2 zavádí strop 2 sloupce.
    // MRT u řazených sloupců automaticky zobrazuje pořadové číslo (1./2.) i směr řazení.
    manualSorting: true,
    enableMultiSort: true,
    maxMultiSortColCount: 2,
    // Multi-sort se přidává vždy (bez nutnosti držet Shift), aby uživatel snadno
    // nastavil druhý sloupec kliknutím. Cap 2 řeší maxMultiSortColCount.
    isMultiSortEvent: () => true,
    onSortingChange: setSorting,
    // COLUMN FILTERING
    manualFiltering: true,
    onColumnFiltersChange: handleColumnFiltersChange,
    columnFilterDisplayMode: 'popover',
    // Add Enter key handler for text/number filter inputs
    muiFilterTextFieldProps: ({ column }) => {
      const variant = columnFilterVariants[column.id];
      const isSelectType = variant === 'select' || variant === 'multi-select';
      return {
        onKeyDown: (event) => {
          if (event.key === 'Enter') {
            commitPendingFilters();
          }
        },
        placeholder: 'Filtr... (Enter)',
        // [fix DAP-017] Pevná šířka u select/multi-select filtrů. Bez ní se šířka pole mění
        // podle obsahu (placeholder ↔ zvolená hodnota, přidávání/odebírání chipů) a filtrační
        // popover/nabídka se kvůli tomu posouvá doleva/doprava (u single-selectu až k levému
        // okraji). Pevná šířka to stabilizuje. U datumových filtrů necháváme výchozí layout.
        ...(isSelectType ? { sx: { minWidth: 240, maxWidth: 240 } } : {}),
        // [fix DAP-017] Multi-select: commit (re-fetch) až při zavření nabídky výběru, aby při
        // zaškrtávání více hodnot nabídka nezmizela/neuskočila. onClose MUI Selectu se pro
        // `multiple` spustí při kliknutí mimo / Escape, tj. když je uživatel s výběrem hotov.
        ...(isCommitOnCloseFilter(column.id)
          ? { SelectProps: { onClose: () => commitFiltersOnClose() } }
          : {})
      };
    },
    // GLOBAL SEARCH FILTER
    enableGlobalFilter: true,
    onGlobalFilterChange: handleGlobalFilterChange,
    muiSearchTextFieldProps: {
      onKeyDown: (event) => {
        if (event.key === 'Enter') {
          commitGlobalFilter();
        }
      },
      placeholder: 'Hledat... (Enter)'
    },
    // ROW SELECTION
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    positionToolbarAlertBanner: 'bottom',
    muiCircularProgressProps: {
      color: 'primary',
      thickness: 5,
      size: 55
    },
    muiSkeletonProps: {
      animation: 'pulse',
      height: 28
    },
    icons: {
      FilterAltIcon: (props: any) => <FilterAltIcon {...props} style={{ color: '#9a0b20' }} />
    },
    // BUTTONS FOR EXPORT, IMPORT AND DELETE PROJECTS
    renderTopToolbarCustomActions: ({ table }) => {
      // Get visible column IDs
      const visibleColumnIds = table
        .getAllColumns()
        .filter((column) => column.getIsVisible() && column.id !== 'mrt-row-select')
        .map((column) => column.id);

      return (
        <Box sx={{ display: 'flex', pb: 2, mt: 2 }}>
          <ExportMenu
            label={'Exportovat vybrané do XSLX'}
            leftSection={<IconFileTypeXls size={26} />}
            actions={[
              {
                name: 'Exportovat vše',
                icon: '',
                count: tableData.totalElements,
                action: () => {
                  if (handleExportRowsExcel) {
                    handleExportRowsExcel(
                      table.getSelectedRowModel().rows,
                      ExportMode.ALL,
                      visibleColumnIds
                    );
                  }
                }
              },
              {
                name: 'Exportovat vybrané',
                icon: '',
                count: table.getSelectedRowModel().rows.length,
                action: () => {
                  if (handleExportRowsExcel) {
                    handleExportRowsExcel(
                      table.getSelectedRowModel().rows,
                      ExportMode.SELECTED,
                      visibleColumnIds
                    );
                  }
                },
                disabled: !table.getIsSomePageRowsSelected() && !table.getIsAllRowsSelected()
              },
              {
                name: 'Exportovat vše na stránce',
                icon: '',
                count: tableData.data.length,
                action: () => {
                  if (handleExportRowsExcel) {
                    handleExportRowsExcel(
                      table.getSelectedRowModel().rows,
                      ExportMode.ALL_ON_PAGE,
                      visibleColumnIds
                    );
                  }
                }
              },
              {
                name: 'Poslední export',
                icon: '',
                count: table.getSelectedRowModel().rows.length,
                action: () => {
                  if (handleExportLastExcel) {
                    handleExportLastExcel(table.getSelectedRowModel().rows);
                  }
                },
                disabled:
                  (!table.getIsSomePageRowsSelected() && !table.getIsAllRowsSelected()) ||
                  !hasExportSettings
              }
            ]}
          />
          <ExportMenu
            label={'Exportovat vybrané do CSV'}
            leftSection={<IconFileTypeCsv size={26} />}
            actions={[
              {
                name: 'Exportovat vše',
                icon: '',
                count: tableData.totalElements,
                action: () => {
                  if (handleExportRowsCsv) {
                    handleExportRowsCsv(
                      table.getSelectedRowModel().rows,
                      ExportMode.ALL,
                      visibleColumnIds
                    );
                  }
                }
              },
              {
                name: 'Exportovat vybrané',
                icon: '',
                count: table.getSelectedRowModel().rows.length,
                action: () => {
                  if (handleExportRowsCsv) {
                    handleExportRowsCsv(
                      table.getSelectedRowModel().rows,
                      ExportMode.SELECTED,
                      visibleColumnIds
                    );
                  }
                },
                disabled: !table.getIsSomePageRowsSelected() && !table.getIsAllRowsSelected()
              },
              {
                name: 'Exportovat vše na stránce',
                icon: '',
                count: tableData.data.length,
                action: () => {
                  if (handleExportRowsCsv) {
                    handleExportRowsCsv(
                      table.getSelectedRowModel().rows,
                      ExportMode.ALL_ON_PAGE,
                      visibleColumnIds
                    );
                  }
                }
              },
              {
                name: 'Poslední export',
                icon: '',
                count: table.getSelectedRowModel().rows.length,
                action: () => {
                  if (handleExportLastCsv) {
                    handleExportLastCsv(table.getSelectedRowModel().rows);
                  }
                },
                disabled:
                  (!table.getIsSomePageRowsSelected() && !table.getIsAllRowsSelected()) ||
                  !hasExportSettings
              }
            ]}
          />
          <Space w={'xl'}></Space>
          {canModify ? (
            <CustomActionIcon
              variant="transparent"
              mt={10}
              mr={10}
              color={'#b01728'}
              badgeCount={table.getSelectedRowModel().rows.length}
              leftSection={<IconTrash size={26} />}
              disabled={!table.getIsSomePageRowsSelected() && !table.getIsAllRowsSelected()}
              tooltipText={'Smazat vybrané projekty'}
              onClick={() => {
                if (deleteProjects) {
                  deleteProjects(table.getSelectedRowModel().rows);
                }
              }}
            />
          ) : (
            <></>
          )}
        </Box>
      );
    }
  });

  // [DAP-019] Auto-scroll tabulky při přetahování sloupce k okraji. Když je tabulka širší než
  // viewport a uživatel táhne sloupec (MRT native HTML5 drag přes grab handle) k levému/pravému
  // okraji scroll kontejneru, plynule odrolujeme tabulku vodorovně, aby dosáhl i na cílové pozice
  // mimo obrazovku. Během rolování si sami udržujeme MRT `hoveredColumn` (cíl přesunu) synchronní
  // se sloupcem pod kurzorem a při puštění MRT provede přesun (reorder) na správné místo.
  // Vždy aktuální instance tabulky pro čtení `refs`/`getState()` z posluchačů (bez zastaralého closure).
  const tableInstanceRef = useRef(materialTable);
  tableInstanceRef.current = materialTable;
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollDirRef = useRef<number>(0); // -1 vlevo, 0 nic, +1 vpravo
  const autoScrollSpeedRef = useRef<number>(0); // px/frame podle blízkosti k okraji
  const lastPointerXRef = useRef<number>(0); // poslední známé X kurzoru (viewport) z dragover

  useEffect(() => {
    const EDGE = 80; // šířka zóny u okraje (px), kde se spouští auto-scroll
    const MAX_SPEED = 24; // max px/frame (u samého okraje)
    const MIN_SPEED = 5; // min px/frame (na vnitřní hraně zóny)

    const stopAutoScroll = () => {
      autoScrollDirRef.current = 0;
      if (autoScrollRafRef.current !== null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
    };

    // [DAP-019] Udržuje MRT `hoveredColumn` (cíl přesunu) synchronní se sloupcem pod kurzorem.
    // Native `dragenter` (jímž MRT jinak `hoveredColumn` nastavuje) se v Chromiu při rolování
    // obsahu pod stojícím kurzorem posílá, ale ve Firefoxu/Safari ne — bez tohoto dorovnání by
    // `hoveredColumn` „zamrzl" a puštění by sloupec přesunulo na špatné místo. Sloupec pod
    // kurzorem spočítáme geometricky z pořadí a šířek viditelných sloupců (bez závislosti na DOM).
    const syncHoveredColumn = (container: HTMLDivElement) => {
      const table = tableInstanceRef.current;
      const rect = container.getBoundingClientRect();
      const contentX = lastPointerXRef.current - rect.left + container.scrollLeft;
      // getVisibleLeafColumns() vrací základní TanStack `Column`; za běhu jsou to MRT sloupce.
      const cols = table.getVisibleLeafColumns() as unknown as MRT_Column<any>[];
      let target: MRT_Column<any> | null = null;
      let offset = 0;
      for (const col of cols) {
        const width = col.getSize();
        if (contentX >= offset && contentX < offset + width) {
          target = col;
          break;
        }
        offset += width;
      }
      // Display sloupce (např. výběrový mrt-row-select) mají enableColumnOrdering === false → null.
      const newHovered = target && target.columnDef.enableColumnOrdering !== false ? target : null;
      const currentId = table.getState().hoveredColumn?.id ?? null;
      if ((newHovered?.id ?? null) !== currentId) {
        table.setHoveredColumn(newHovered);
      }
    };

    const step = () => {
      const container = tableInstanceRef.current.refs.tableContainerRef.current;
      const dir = autoScrollDirRef.current;
      if (!container || dir === 0) {
        autoScrollRafRef.current = null;
        return;
      }
      const maxScroll = container.scrollWidth - container.clientWidth;
      // Na kraji obsahu už není kam rolovat — smyčku zastavíme.
      if (
        (dir < 0 && container.scrollLeft <= 0) ||
        (dir > 0 && container.scrollLeft >= maxScroll)
      ) {
        autoScrollRafRef.current = null;
        return;
      }
      container.scrollLeft += dir * autoScrollSpeedRef.current;
      syncHoveredColumn(container);
      autoScrollRafRef.current = requestAnimationFrame(step);
    };

    const handleDragOver = (event: DragEvent) => {
      const table = tableInstanceRef.current;
      const container = table.refs.tableContainerRef.current;
      // Reagujeme jen během přetahování sloupce.
      if (!container || !table.getState().draggingColumn) {
        stopAutoScroll();
        return;
      }
      const rect = container.getBoundingClientRect();
      const x = event.clientX;
      lastPointerXRef.current = x;
      let dir = 0;
      let intensity = 0;
      if (x < rect.left + EDGE) {
        dir = -1;
        intensity = (rect.left + EDGE - x) / EDGE;
      } else if (x > rect.right - EDGE) {
        dir = 1;
        intensity = (x - (rect.right - EDGE)) / EDGE;
      }
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (dir < 0 && container.scrollLeft <= 0) dir = 0;
      if (dir > 0 && container.scrollLeft >= maxScroll) dir = 0;

      if (dir === 0) {
        stopAutoScroll();
        return;
      }
      autoScrollDirRef.current = dir;
      autoScrollSpeedRef.current = MIN_SPEED + Math.min(1, intensity) * (MAX_SPEED - MIN_SPEED);
      if (autoScrollRafRef.current === null) {
        autoScrollRafRef.current = requestAnimationFrame(step);
      }
    };

    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragend', stopAutoScroll);
    document.addEventListener('drop', stopAutoScroll);
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragend', stopAutoScroll);
      document.removeEventListener('drop', stopAutoScroll);
      stopAutoScroll();
    };
  }, []);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPagination({
      ...pagination,
      pageIndex: newPage
    });
    setRowSelection({});
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPagination({
      pageIndex: 0,
      pageSize: parseInt(event.target.value, 10)
    });
    setRowSelection({});
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
          <Flex justify={'flex-end'}>
            <CustomButton
              variant={'transparent'}
              onClick={clearTableFilters}
              label={'Vymazat filtry'}></CustomButton>
          </Flex>
          <Space h="md" />
          <MaterialReactTable table={materialTable} />
          <TablePagination
            component="div"
            count={tableData.totalElements}
            page={pagination.pageIndex}
            onPageChange={handleChangePage}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100, 500]}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </>
  );
};

export default MaterialTable;
